import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useBridgeApi } from '@/api/BridgeApi';
import { useBridgewayApi } from '@/api/BridgewayApi';
import { useUserId } from '@/auth/UserIdContext';
import { logger } from '@/logger';

import type {
	StrategyListItem,
	StrategyGenerationProgressEvent,
	Strategy,
} from '../../../../bridge/types/strategy.ts';

import {
	formatMetricValue,
	metricGoalProgress,
	selectPrimaryMetric,
} from './strategyFormat';

// Drives the OPEN detail view's live progress narration. The run's `events` feed is fetched by
// the same refresh (see useStrategyDetail), and no push covers it: there is no strategy-progress
// action in the bridge, and the backend's earliest generation signal is terminal. Gated on one
// open detail view of a generating run — not the always-mounted dock — so this is not the
// always-on cost the push signal replaced. Distinct from WATCHDOG_NUDGE_INTERVAL_MS: two jobs.
const PROGRESS_FEED_POLL_INTERVAL_MS = 4_000;

// Backoff ladder for a refetch that was triggered by a pushed revision and failed. Such a fetch
// has to re-drive itself, because nothing else will: the counter bumps once per change, and a
// settled run has no timer behind it. Finite on purpose — long enough to ride out a blip, short
// of a standing retry loop against an API that is genuinely down.
const REVISION_RETRY_DELAYS_MS = [2_000, 8_000, 20_000, 30_000];

// A slow nudge, NOT a freshness mechanism — completion arrives by push (report_strategies_changed).
// This exists only to trigger the server's read-time watchdog: a run that dies without reaching
// _finalize_node emits nothing, and `_merge_control` (api/routes/strategies.py) only reads it as
// failed when someone GETs the list. Without this, such a run would spin forever in an open tab.
// Do not lower it to chase completion latency.
const WATCHDOG_NUDGE_INTERVAL_MS = 60_000;

export interface UseStrategies {
	strategies: StrategyListItem[];
	loading: boolean;
	error: string | null;
	refresh: () => Promise<void>;
}

// Strategies list: fetch once on mount, then refetch when the bridge says this user's
// strategies changed (`user_state.strategy_dock_last_updated`). Explicit {loading,error}, never a
// silent .catch(()=>[]) — that's the ADO-1594 "empty when it actually failed" bug.
export function useStrategies(opts: { enabled?: boolean } = {}): UseStrategies {
	const { enabled = true } = opts;
	const userId = useUserId();
	const bridgewayApi = useBridgewayApi();
	const bridgeApi = useBridgeApi();
	const [strategies, setStrategies] = useState<StrategyListItem[]>([]);
	// When gated off, nothing loads and there is nothing to wait for → not loading.
	const [loading, setLoading] = useState(enabled);
	const [error, setError] = useState<string | null>(null);

	const fetchRef = useRef<() => Promise<boolean>>(async () => false);
	// Without the 4s poll to paper over it, a bump-triggered refetch and a mutation's refresh()
	// can interleave and leave the OLDER list rendered. Each fetch takes a ticket and only the
	// latest writes, mirroring useStrategyDetail's guard below.
	const fetchTicketRef = useRef(0);

	// Resolves true only when fresh rows were actually written to state. The revision effect
	// needs that distinction; `refresh` below is the void-returning public form.
	const fetchList = useCallback(async () => {
		// Gated off: skip the endpoint — it isn't deployed everywhere, so a request would 404.
		if (!enabled || !userId) return false;
		const ticket = ++fetchTicketRef.current;
		try {
			const data = await bridgewayApi.get<StrategyListItem[]>(
				`/api/user/${userId}/strategies/`,
			);
			if (ticket !== fetchTicketRef.current) return false; // superseded
			setStrategies(Array.isArray(data) ? data : []);
			setError(null);
			return true;
		} catch (err) {
			if (ticket !== fetchTicketRef.current) return false;
			logger.error('Failed to fetch strategies', err);
			setError('We couldn’t load your strategies.');
			return false;
		} finally {
			if (ticket === fetchTicketRef.current) setLoading(false);
		}
	}, [enabled, userId, bridgewayApi]);

	fetchRef.current = fetchList;

	const refresh = useCallback(async () => {
		await fetchList();
	}, [fetchList]);

	useEffect(() => {
		if (!enabled || !userId) return undefined;
		void fetchRef.current();
	}, [enabled, userId]);

	const revision = bridgeApi.user_state?.strategy_dock_last_updated ?? 0;
	// null until the first observed value, which the mount effect above already fetched — so the
	// baseline is recorded without a second request. Re-baselined on a user switch.
	const lastSeenRevisionRef = useRef<number | null>(null);
	useEffect(() => {
		lastSeenRevisionRef.current = null;
	}, [userId]);
	useEffect(() => {
		if (!enabled || !userId) return;
		if (lastSeenRevisionRef.current === null) {
			lastSeenRevisionRef.current = revision;
			return;
		}
		if (revision === lastSeenRevisionRef.current) return;
		const target = revision;
		// Advance ONLY after the fetch resolves: refresh() swallows errors and keeps the last
		// good list, so advancing eagerly would let one transient failure consume the signal and
		// strand a stale list with no fast poll left to recover it.
		void fetchRef.current().then((ok) => {
			if (ok) lastSeenRevisionRef.current = target;
		});
	}, [enabled, userId, revision]);

	const anyGenerating = strategies.some((s) => s.status === 'generating');
	useEffect(() => {
		if (!enabled || !anyGenerating) return undefined;
		const id = setInterval(() => {
			void fetchRef.current();
		}, WATCHDOG_NUDGE_INTERVAL_MS);
		return () => clearInterval(id);
	}, [enabled, anyGenerating]);

	return { strategies, loading, error, refresh };
}

export interface UseStrategyDetail {
	run: Strategy | null;
	events: StrategyGenerationProgressEvent[];
	loading: boolean;
	notFound: boolean;
	error: string | null;
	refresh: () => Promise<void>;
}

// 'gone' is a 404 — the backend's settled answer, so retrying it is pointless. 'failed' is
// everything else, which may well succeed on a second ask.
type DetailFetchOutcome = 'loaded' | 'failed' | 'gone';

// A single strategy run for the detail view: loads the run + its progress feed, polling
// every 4s while generating. A 404 → notFound (genuinely gone), distinct from !run
// (still loading), so a delete-then-back never hangs on a spinner.
export function useStrategyDetail(id: string | null): UseStrategyDetail {
	const userId = useUserId();
	const bridgewayApi = useBridgewayApi();
	const bridgeApi = useBridgeApi();
	const [run, setRun] = useState<Strategy | null>(null);
	const [events, setEvents] = useState<StrategyGenerationProgressEvent[]>([]);
	const [loading, setLoading] = useState(true);
	const [notFound, setNotFound] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const fetchRef = useRef<() => Promise<DetailFetchOutcome>>(
		async () => 'failed',
	);
	// Guards a stale write: since `run` isn't blanked on an id change, a slow fetch for a
	// previous sibling could resolve late and overwrite the current run. Each fetch captures
	// its id and bails after each await once superseded, so only the latest hop writes.
	const currentIdRef = useRef<string | null>(id);
	currentIdRef.current = id;

	// Reports what the request settled as, not just whether it wrote state: the revision effect
	// retries a 'failed' fetch and must not retry a run the backend says is gone. `refresh` below
	// is the void-returning public form.
	const fetchDetail = useCallback(async (): Promise<DetailFetchOutcome> => {
		if (!userId || !id) return 'failed';
		try {
			const fresh = await bridgewayApi.get<Strategy>(
				`/api/user/${userId}/strategies/${id}`,
			);
			if (currentIdRef.current !== id) return 'failed'; // superseded by a later hop
			setRun(fresh);
			setNotFound(false);
			setError(null);
			// Progress feed is only present around generating; a failure here is non-fatal.
			try {
				const prog = await bridgewayApi.get<{
					events: StrategyGenerationProgressEvent[];
				}>(`/api/user/${userId}/strategies/${id}/progress`);
				if (currentIdRef.current !== id) return 'failed';
				setEvents(Array.isArray(prog?.events) ? prog.events : []);
			} catch {
				/* progress is best-effort */
			}
			return 'loaded';
		} catch (err) {
			if (currentIdRef.current !== id) return 'failed'; // superseded — don't strand on a stale error
			const status = (err as { status?: number })?.status;
			if (status === 404) {
				setNotFound(true);
				return 'gone';
			}
			logger.error('Failed to fetch strategy', err);
			setError('We couldn’t load this strategy.');
			return 'failed';
		} finally {
			if (currentIdRef.current === id) setLoading(false);
		}
	}, [userId, id, bridgewayApi]);

	fetchRef.current = fetchDetail;

	const refresh = useCallback(async () => {
		await fetchDetail();
	}, [fetchDetail]);

	useEffect(() => {
		// Don't blank run/events on an id change: it would flash a spinner over the whole
		// detail view on every sibling hop. Keep the prior run visible until the fresh one
		// resolves — StrategyDetail shows the spinner only on a true cold load, not a swap.
		setLoading(true);
		setNotFound(false);
		setError(null);
		if (!userId || !id) return undefined;
		void fetchRef.current();
	}, [userId, id]);

	// Kept deliberately: this refresh also fetches the run's `events`, which is the live
	// progress narration StrategyDetail renders. No push covers it — the bridge has no
	// strategy-progress action and the backend's first generation signal is terminal — so
	// deleting this would silently freeze the feed. Activation, by contrast, now arrives by
	// push, so its separate poll is gone.
	const generating = run?.status === 'generating';
	useEffect(() => {
		if (!generating) return undefined;
		const timer = setInterval(() => {
			void fetchRef.current();
		}, PROGRESS_FEED_POLL_INTERVAL_MS);
		return () => clearInterval(timer);
	}, [generating]);

	const revision = bridgeApi.user_state?.strategy_dock_last_updated ?? 0;
	// null until the first observed value, which the mount effect above already fetched — so the
	// baseline is recorded without a second request. Re-baselined on a user or strategy switch.
	const lastSeenRevisionRef = useRef<number | null>(null);
	useEffect(() => {
		lastSeenRevisionRef.current = null;
	}, [userId, id]);
	useEffect(() => {
		if (!userId || !id) return undefined;
		if (lastSeenRevisionRef.current === null) {
			lastSeenRevisionRef.current = revision;
			return undefined;
		}
		if (revision === lastSeenRevisionRef.current) return undefined;
		const target = revision;
		let cancelled = false;
		let timer: ReturnType<typeof setTimeout> | undefined;
		// The push is the only thing that refreshes a settled run: the 4s timer runs while
		// generating, and nothing else covers activation. So a transient failure here has to be
		// retried from inside this effect — it is keyed on the revision, and the revision that
		// failed to load is precisely the value that has stopped changing, so re-running on a
		// dependency is not available. Without this the view keeps rendering the previous run for
		// as long as it stays open, still reading "Setting up…".
		const attempt = (n: number) => {
			void fetchRef.current().then((outcome) => {
				if (cancelled) return;
				// A deleted run counts as answered: the view switches to "no longer available",
				// and asking again would only 404 again.
				if (outcome !== 'failed') {
					lastSeenRevisionRef.current = target;
					return;
				}
				const delay = REVISION_RETRY_DELAYS_MS[n];
				if (delay === undefined) return;
				timer = setTimeout(() => attempt(n + 1), delay);
			});
		};
		attempt(0);
		// A newer revision, a strategy hop, or an unmount supersedes the whole ladder.
		return () => {
			cancelled = true;
			clearTimeout(timer);
		};
	}, [userId, id, revision]);

	return { run, events, loading, notFound, error, refresh };
}

export interface StrategyCardNorthStar {
	name: string;
	// Target readout ("200 followers" / "3%") for the ratio label.
	readout: string;
	// Localized target/baseline numbers for the "84 / 200" ratio.
	target: number;
	baseline?: number;
	// Static progress toward goal (NOT live) — 0 when there is no real baseline.
	pct: number;
	hasStart: boolean;
}

// One campaign's "the move" — the concrete thing that actually happens, which is what an owner
// deciding whether to activate needs to read. Deliberately carries NO readiness/lane field: the
// card shows every campaign, so there is nothing here to filter on.
export interface StrategyMove {
	id: string;
	title: string;
	move: string;
}

// What a card needs beyond the slim list row: every campaign's move, and the north-star metric +
// its static progress. All from ONE full-run GET — the slim list omits strategy/campaigns.
export interface StrategyCardData {
	state: 'loading' | 'ready' | 'error';
	moves: StrategyMove[];
	northStar: StrategyCardNorthStar | null;
	refetch: () => void;
}

// Per-card enrichment. Fetches the full run ONCE when enabled + the run has a plan worth
// showing (complete or active). Explicit loading/error so a fetch failure is never
// mistaken for "no campaigns" (a card must not hide a real Activate behind a silent error).
export function useStrategyCard(
	id: string,
	status: Strategy['status'],
	enabled: boolean,
): StrategyCardData {
	const userId = useUserId();
	const bridgewayApi = useBridgewayApi();
	// An ACTIVE run has a plan too — gating on 'complete' alone left an activated strategy
	// rendering an empty card (no statement, no north-star, no campaign rollup).
	const shouldFetch =
		enabled && !!userId && (status === 'complete' || status === 'active');
	const [run, setRun] = useState<Strategy | null>(null);
	const [state, setState] = useState<'loading' | 'ready' | 'error'>(
		shouldFetch ? 'loading' : 'ready',
	);
	const [nonce, setNonce] = useState(0);

	useEffect(() => {
		if (!shouldFetch) {
			setState('ready');
			return undefined;
		}
		let cancelled = false;
		setState('loading');
		void bridgewayApi
			.get<Strategy>(`/api/user/${userId}/strategies/${id}`)
			.then((fresh) => {
				if (cancelled) return;
				setRun(fresh);
				setState('ready');
			})
			.catch((err) => {
				if (cancelled) return;
				logger.error('Failed to load strategy card', err);
				setState('error');
			});
		return () => {
			cancelled = true;
		};
	}, [shouldFetch, userId, id, bridgewayApi, nonce]);

	const refetch = useCallback(() => setNonce((n) => n + 1), []);

	return useMemo<StrategyCardData>(() => {
		// Every campaign gets a row — including one whose `aim` came back blank, which keeps the
		// card's campaign count honest against the strategy screen (the view degrades the body
		// instead of dropping the campaign).
		const moves: StrategyMove[] = (run?.campaign_proposals ?? []).map((c) => ({
			// By id only, matching the backend. There used to be a positional c{n} fallback for
			// legacy id-less campaigns; it went when activation state moved onto the campaign,
			// because a position is not stable across a drop. This is a row's React key.
			id: c.id,
			title: c.title?.trim() || 'Campaign',
			move: c.aim?.trim() ?? '',
		}));

		const primary = selectPrimaryMetric(run?.direction?.metrics);
		let northStar: StrategyCardNorthStar | null = null;
		if (primary) {
			const { target, unit, baseline } = primary.metric.metric;
			const { pct, hasStart } = metricGoalProgress(target, baseline);
			northStar = {
				name: primary.metric.metric.name,
				readout: formatMetricValue(target, unit),
				target,
				baseline,
				pct,
				hasStart,
			};
		}

		return {
			// `state` is set in a passive effect, so it lags a SYNCHRONOUS status flip
			// ('generating'→'complete' from the 4s list poll; DockMenu's flat child list means no
			// remount). Derive 'loading' from (shouldFetch && !run) or the card paints a false
			// "couldn't load" on that frame.
			state:
				state === 'error' ? 'error' : shouldFetch && !run ? 'loading' : state,
			moves,
			northStar,
			refetch,
		};
	}, [run, state, shouldFetch, refetch]);
}
